/**
 * Browser Notification Service
 * Handles permission requests and showing notifications
 * Supports both PWA (Web Push) and Native (Capacitor) apps
 * 
 * AUTO-SUBSCRIPTION: Automatically subscribes users to push notifications
 * on login when supported by the browser/device.
 */

import { isNativePlatform, PushNotificationService } from './capacitor';

// Helper to convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const notificationService = {
  /**
   * Check if this is iOS Safari PWA
   */
  isIOSPWA() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.navigator.standalone === true || 
                         window.matchMedia('(display-mode: standalone)').matches;
    return isIOS && isStandalone;
  },
  
  /**
   * Check if running as native Capacitor app
   */
  isNativeApp() {
    return isNativePlatform();
  },

  /**
   * Check if push notifications are supported
   */
  isSupported() {
    return 'Notification' in window && 
           'serviceWorker' in navigator && 
           'PushManager' in window;
  },

  /**
   * Request notification permission from the user
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  },

  /**
   * Subscribe to push notifications
   * Handles both PWA (Web Push) and Native (Capacitor) apps
   */
  async subscribeToPush(apiBaseUrl) {
    try {
      console.log('📱 Starting push subscription process...');
      console.log('  - Native App:', this.isNativeApp());
      console.log('  - iOS PWA:', this.isIOSPWA());
      console.log('  - Service Worker supported:', 'serviceWorker' in navigator);
      console.log('  - PushManager supported:', 'PushManager' in window);
      
      // For native Capacitor apps, use native push
      if (this.isNativeApp()) {
        console.log('📱 Using Native (Capacitor) Push Notifications');
        return await this.subscribeNativePush(apiBaseUrl);
      }
      
      // For web/PWA, use Web Push API
      console.log('📱 Using Web Push API');
      console.log('  - Notification permission:', typeof Notification !== 'undefined' ? Notification.permission : 'N/A');
      
      // Check if service worker is available
      if (!('serviceWorker' in navigator)) {
        console.error('❌ Service Worker not supported');
        throw new Error('Service Worker not supported in this browser');
      }

      if (!('PushManager' in window)) {
        console.error('❌ Push notifications not supported');
        throw new Error('Push notifications not supported in this browser');
      }

      // Request notification permission
      console.log('📱 Step 1: Requesting permission...');
      const permissionGranted = await this.requestPermission();
      console.log('  - Permission result:', permissionGranted);
      
      if (!permissionGranted) {
        console.error('❌ Notification permission denied');
        throw new Error('Notification permission denied');
      }

      // Register service worker if not already registered
      console.log('📱 Step 2: Registering service worker...');
      let registration;
      try {
        // Check if already registered
        registration = await navigator.serviceWorker.getRegistration('/');
        if (registration) {
          console.log('  - Using existing SW registration');
        } else {
          console.log('  - Registering new service worker...');
          registration = await navigator.serviceWorker.register('/service-worker.js', {
            scope: '/'
          });
          console.log('  - Service Worker registered');
        }
        
        // Wait for it to be ready
        console.log('  - Waiting for SW to be ready...');
        registration = await navigator.serviceWorker.ready;
        console.log('  - Service Worker is ready:', registration.active?.state);
      } catch (swError) {
        console.error('❌ Service Worker registration failed:', swError);
        throw new Error(`Service Worker failed: ${swError.message}`);
      }

      // Get VAPID public key from backend with retry and fallback
      console.log('📱 Step 3: Getting VAPID key from backend...');
      console.log('  - API URL:', apiBaseUrl);
      
      // HARDCODED FALLBACK - VAPID key never changes, this prevents network failures from blocking notifications
      const FALLBACK_VAPID_KEY = 'BBMBQWmDMp_eZK3TBVqIR0mJZxoMLPtPqndJ3tJ5jOcd5ZlufKK57S3ef9HSXw_h5zzhsE9pSMhqu2L7OAswe5Y';
      
      let publicKey = null;
      let lastError = null;
      
      // Try to fetch from backend first (2 attempts), then use fallback
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          console.log(`  - Attempt ${attempt}/2...`);
          const vapidResponse = await fetch(`${apiBaseUrl}/api/push/vapid-public-key`, {
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              'Cache-Control': 'no-cache'
            }
          });
          
          console.log(`  - Response status: ${vapidResponse.status}`);
          
          if (vapidResponse.ok) {
            try {
              const vapidData = await vapidResponse.json();
              console.log('  - VAPID response:', JSON.stringify(vapidData));
              
              if (vapidData.publicKey) {
                publicKey = vapidData.publicKey;
                console.log(`  - VAPID key received: ${publicKey.substring(0, 20)}...`);
                break; // Success!
              }
            } catch (parseError) {
              console.error('❌ Failed to parse JSON:', parseError);
            }
          }
          
          // Wait before retry
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 500));
          }
        } catch (fetchError) {
          console.error(`❌ Attempt ${attempt} failed:`, fetchError.message);
          lastError = fetchError;
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 500));
          }
        }
      }
      
      // Use fallback if fetch failed
      if (!publicKey) {
        console.log('⚠️ Using fallback VAPID key (network issue detected)');
        publicKey = FALLBACK_VAPID_KEY;
      }

      // Check for existing subscription first
      console.log('📱 Step 4: Checking existing subscription...');
      let subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('  - Found existing subscription, unsubscribing first...');
        await subscription.unsubscribe();
        subscription = null;
      }

      // Subscribe to push notifications
      console.log('📱 Step 5: Creating push subscription...');
      console.log('  - ApplicationServerKey length:', urlBase64ToUint8Array(publicKey).length);
      
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
        console.log('✅ Push subscription created!');
        console.log('  - Endpoint:', subscription.endpoint.substring(0, 50) + '...');
      } catch (pushError) {
        console.error('❌ PushManager.subscribe failed:', pushError);
        console.error('  - Error name:', pushError.name);
        console.error('  - Error message:', pushError.message);
        
        // iOS-specific error handling
        if (pushError.name === 'NotAllowedError') {
          throw new Error('Push notifications were blocked. Please check your iOS Settings → Notifications → Safari/LifeFlo');
        } else if (pushError.name === 'AbortError') {
          throw new Error('Push subscription was cancelled. Please try again.');
        } else {
          throw new Error(`Push subscription failed: ${pushError.message}`);
        }
      }

      // Send subscription to backend with RETRY
      console.log('📱 Step 6: Saving subscription to backend...');
      const subscriptionJSON = subscription.toJSON();
      console.log('  - Subscription data:', JSON.stringify(subscriptionJSON).substring(0, 100) + '...');
      
      // Retry up to 3 times
      lastError = null; // Reuse the lastError variable from VAPID fetch
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          console.log(`  - Attempt ${attempt}/3...`);
          const subscribeResponse = await fetch(`${apiBaseUrl}/api/push/subscribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ subscription: subscriptionJSON })
          });
          
          if (!subscribeResponse.ok) {
            const errorText = await subscribeResponse.text();
            console.error(`❌ Attempt ${attempt} failed:`, subscribeResponse.status, errorText);
            lastError = new Error(`Failed to save subscription: ${subscribeResponse.status} - ${errorText}`);
            
            // If 401 unauthorized, don't retry
            if (subscribeResponse.status === 401) {
              throw new Error('Not authenticated. Please log in again.');
            }
            
            // Wait before retry
            if (attempt < 3) await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          
          const result = await subscribeResponse.json();
          console.log('✅ Subscription saved to backend:', result);
          return subscription;
          
        } catch (fetchError) {
          console.error(`❌ Attempt ${attempt} error:`, fetchError);
          lastError = fetchError;
          if (attempt < 3) await new Promise(r => setTimeout(r, 1000));
        }
      }
      
      // All retries failed
      throw lastError || new Error('Failed to save subscription after 3 attempts');
      
    } catch (error) {
      console.error('❌ Error in subscribeToPush:', error);
      throw error; // Re-throw so caller can handle
    }
  },

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribeFromPush(apiBaseUrl) {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        
        // Notify backend
        await fetch(`${apiBaseUrl}/api/push/unsubscribe`, {
          method: 'POST',
          credentials: 'include'
        });
      }

      return true;
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      return false;
    }
  },

  /**
   * Check if user is subscribed to push notifications
   */
  async isPushSubscribed() {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      return subscription !== null;
    } catch (error) {
      console.error('Error checking push subscription:', error);
      return false;
    }
  },

  /**
   * Alias for isPushSubscribed - checks if subscribed
   */
  async isSubscribed() {
    return this.isPushSubscribed();
  },

  /**
   * Get current permission status
   */
  getPermission() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission;
  },

  /**
   * Show a notification
   */
  async showNotification(title, options = {}) {
    const hasPermission = await this.requestPermission();
    
    if (!hasPermission) {
      console.log('Notification permission denied');
      return null;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      // Auto close after 10 seconds if not clicked
      setTimeout(() => notification.close(), 10000);
      
      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  },

  /**
   * Show water reminder notification
   */
  async showWaterReminder(message) {
    return this.showNotification(message.title, {
      body: message.description,
      icon: '/logo192.png',
      tag: 'water-reminder',
      requireInteraction: true,
      data: {
        type: 'water-reminder'
      }
    });
  },
  
  /**
   * Subscribe to native push notifications (Capacitor)
   * Used for iOS/Android native apps
   */
  async subscribeNativePush(apiBaseUrl) {
    try {
      console.log('📱 Native Push: Starting registration...');
      
      // Request permission and register
      const result = await PushNotificationService.register();
      
      if (!result.success) {
        console.error('❌ Native push registration failed:', result.message);
        throw new Error(result.message);
      }
      
      console.log('📱 Native Push: Registered successfully, waiting for token...');
      
      // Listen for registration token
      return new Promise((resolve, reject) => {
        PushNotificationService.addListeners(
          // On registration - save token to backend
          async (token) => {
            console.log('✅ Native Push: Got registration token');
            
            try {
              // Save FCM/APNs token to backend
              const response = await fetch(`${apiBaseUrl}/api/push/subscribe-native`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ 
                  token: token.value,
                  platform: navigator.userAgent.includes('iPhone') ? 'ios' : 'android'
                })
              });
              
              if (response.ok) {
                console.log('✅ Native Push: Token saved to backend');
                resolve(token);
              } else {
                throw new Error('Failed to save token to backend');
              }
            } catch (error) {
              console.error('❌ Native Push: Error saving token:', error);
              reject(error);
            }
          },
          // On notification received
          (notification) => {
            console.log('📬 Native Push: Notification received:', notification);
          },
          // On error
          (error) => {
            console.error('❌ Native Push: Registration error:', error);
            reject(new Error(error.error || 'Registration failed'));
          }
        );
        
        // Timeout after 10 seconds
        setTimeout(() => {
          reject(new Error('Push registration timed out'));
        }, 10000);
      });
    } catch (error) {
      console.error('❌ Native Push subscription error:', error);
      throw error;
    }
  },

  /**
   * AUTO-SUBSCRIBE: Automatically subscribe user to push notifications after login
   * This is the main function to call after successful authentication
   * 
   * IMPORTANT: Browsers require user gesture to show permission prompt.
   * If permission is 'default', we return needsUserGesture=true so the UI can show
   * a clickable prompt that the user can tap to enable notifications.
   * 
   * @param {string} apiBaseUrl - Backend API base URL
   * @returns {Object} - { success: boolean, subscribed: boolean, needsUserGesture: boolean, message: string }
   */
  async autoSubscribeAfterLogin(apiBaseUrl) {
    console.log('🔔 AUTO-SUBSCRIBE: Starting automatic push notification setup...');
    
    const result = {
      success: false,
      subscribed: false,
      permissionDenied: false,
      needsUserGesture: false,
      message: ''
    };

    try {
      // Check if push notifications are supported
      if (!this.isSupported() && !this.isNativeApp()) {
        result.message = 'Push notifications are not supported on this device/browser.';
        console.log('⚠️ AUTO-SUBSCRIBE:', result.message);
        return result;
      }

      // Check current permission state (don't prompt yet)
      const currentPermission = this.getPermission();
      console.log('📱 AUTO-SUBSCRIBE: Current permission state:', currentPermission);

      // If already denied, user needs to enable from browser settings
      if (currentPermission === 'denied') {
        result.permissionDenied = true;
        result.message = 'Notifications are blocked. Please enable them in your browser/device settings.';
        console.log('⚠️ AUTO-SUBSCRIBE:', result.message);
        return result;
      }

      // Check if already subscribed
      const alreadySubscribed = await this.isPushSubscribed();
      if (alreadySubscribed) {
        console.log('✅ AUTO-SUBSCRIBE: User already has a push subscription');
        
        // Update backend to ensure notifications_enabled = true
        try {
          await fetch(`${apiBaseUrl}/api/push/auto-enable`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
        } catch (e) {
          console.warn('Could not update notification preferences:', e);
        }
        
        result.success = true;
        result.subscribed = true;
        result.message = 'Already subscribed to notifications.';
        localStorage.setItem('notifications_enabled', 'true');
        return result;
      }

      // If permission is 'granted' (user previously allowed), subscribe silently
      if (currentPermission === 'granted') {
        console.log('📱 AUTO-SUBSCRIBE: Permission already granted, subscribing...');
        try {
          const subscription = await this.subscribeToPush(apiBaseUrl);
          
          if (subscription) {
            console.log('✅ AUTO-SUBSCRIBE: Successfully subscribed!');
            
            // Update backend to enable notifications for this user
            try {
              await fetch(`${apiBaseUrl}/api/push/auto-enable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
              });
            } catch (e) {
              console.warn('Could not update notification preferences:', e);
            }
            
            result.success = true;
            result.subscribed = true;
            result.message = 'Successfully subscribed to notifications!';
            localStorage.setItem('notifications_enabled', 'true');
            return result;
          }
        } catch (subscribeError) {
          console.error('❌ AUTO-SUBSCRIBE: Subscription failed:', subscribeError);
          result.message = subscribeError.message || 'Failed to subscribe.';
          return result;
        }
      }

      // If permission is 'default' (not asked yet), we CANNOT prompt without user gesture
      // Return flag so UI can show a clickable prompt
      if (currentPermission === 'default') {
        console.log('📱 AUTO-SUBSCRIBE: Permission not yet requested - needs user gesture');
        result.needsUserGesture = true;
        result.message = 'Tap to enable notifications';
        return result;
      }

      result.message = 'Could not determine notification status.';
      return result;

    } catch (error) {
      console.error('❌ AUTO-SUBSCRIBE: Error:', error);
      result.message = error.message || 'Failed to set up notifications.';
      return result;
    }
  },

  /**
   * Subscribe with user gesture - call this when user clicks "Enable Notifications"
   * This MUST be called from a user-initiated event (click, tap)
   */
  async subscribeWithUserGesture(apiBaseUrl) {
    console.log('🔔 SUBSCRIBE: User clicked to enable notifications...');
    
    try {
      const subscription = await this.subscribeToPush(apiBaseUrl);
      
      if (subscription) {
        console.log('✅ SUBSCRIBE: Successfully subscribed!');
        
        // Update backend to enable notifications
        try {
          await fetch(`${apiBaseUrl}/api/push/auto-enable`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
        } catch (e) {
          console.warn('Could not update notification preferences:', e);
        }
        
        localStorage.setItem('notifications_enabled', 'true');
        return { success: true, message: 'Notifications enabled!' };
      }
      
      return { success: false, message: 'Failed to subscribe.' };
    } catch (error) {
      console.error('❌ SUBSCRIBE: Failed:', error);
      
      // Check if permission was denied
      if (error.message?.includes('denied') || error.message?.includes('blocked')) {
        return { 
          success: false, 
          permissionDenied: true,
          message: 'Notifications blocked. Enable in browser settings.' 
        };
      }
      
      return { success: false, message: error.message || 'Failed to enable notifications.' };
    }
  },

  /**
   * Check if user can receive notifications (has valid subscription + enabled)
   */
  async canReceiveNotifications() {
    try {
      const isSubscribed = await this.isPushSubscribed();
      const permission = this.getPermission();
      const enabled = localStorage.getItem('notifications_enabled') === 'true';
      
      return isSubscribed && permission === 'granted' && enabled;
    } catch (error) {
      return false;
    }
  }
};

export default notificationService;
